/* eslint-disable max-len */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import ReactCalendar from 'react-calendar'

import CheckSvg from '../images/check.svg'
import UncheckSvg from '../images/uncheck.svg'

import { colors, fonts, device } from '../styles/config'
import { brueckendeckUrl, sonnendeckUrl, inselgartenUrl, theaterdeckUrl } from '../../config/website'
import { compareDates } from '../utils/time'

const StyledCalendar = styled(ReactCalendar)`
  border: none !important;
  flex: 1 0 370px;
  width: initial !important;
  font-style: normal;
  font-weight: 400;

  * {
    font-family: ${ fonts.default }, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
    font-style: normal;
    font-weight: 400;
  }

  @media ${ device.sm } {
    margin-top: 2rem;
  }

  abbr {
    border: none !important;
    cursor: pointer !important;
  }

  /* top bar */
  .react-calendar__navigation {
    border-bottom: 1px solid rgba(40,47,54,0.15);
    font-family: Roboto;
    height: 4.6875rem;
    padding: 0 2rem;

    @media ${ device.md } {
      padding: 0 1rem;
    }

    @media ${ device.sm } {
      padding: 0;
    }

    [disabled] {
      color: ${ colors.calendar };
      background-color: initial;
      font-weight: bold;
      font-size: 0.875rem;
      letter-spacing: 3.27px;
      text-transform: uppercase;
    }

    button:enabled:hover,
    button:enabled:focus {
      background-color: initial;
    }

    .react-calendar__navigation__arrow {
      font-size: 1.5rem;
      color: ${ colors.calendar };
      padding-right: 7px;
      padding-left: 7px;

      &[disabled] {
        color: ${ colors.neutralDark };
      }
    }
  }

  /* weekday names and days group */
  .react-calendar__month-view  {
    padding: 0 2rem;

    @media ${ device.md } {
      padding: 0 1rem;
    }

    @media ${ device.sm } {
      padding: 0;
    }
  }

  /* Weekday names */
  .react-calendar__month-view__weekdays {
    abbr {
      text-transform: initial;
      font-size: 0.75rem;
      line-height: 1.25;
      color: #D0D0D7;
    }
  }

  .react-calendar__tile {
    width: 41px;
    height: 41px;
    overflow: initial !important;
    border-radius: 50%;
    font-family: Roboto;
    color: ${ colors.calendar };
    font-size: 0.875rem;
    letter-spacing: 0.53px;
    line-height: 1.857;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    background: initial !important;

    abbr {
      height: 31px;
      width: 31px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .react-calendar__month-view__days__day--weekend {
    color: ${ colors.calendar };
  }

  .react-calendar__tile--now {
    color: ${ colors.calendar };
  }

  .react-calendar__tile--active {
    position: relative;
    color: white;
    background: initial;

    abbr {
      border-radius: 50%;
      background-color: ${ colors.calendar };
    }

    ${ props => props.statusRowVisible && css`
      &:before {
        content: '';
        position: absolute;
        left: 50%;
        bottom: -10px;
        margin-left: -10px;
        width: 0; 
        height: 0; 
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid rgba(40,47,54,0.15);
      }

      &:after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: -11px;
        margin-left: -9px;
        width: 0;
        height: 0;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-bottom: 9px solid white;
        z-index: 1;
      }
    ` }
  }

  .react-calendar__tile:disabled {
    background: initial;

    abbr {
      color: #D0D0D7;
      font-weight: normal !important;
      cursor: initial;
      text-decoration: line-through;
    }
  }
  
  .react-calendar__month-view__days__day--neighboringMonth {
    abbr {
      font-weight: normal !important;
    }
  }
  

  .calendar-status-row {
    display: block;
    height: 120px;
    margin-top: 10px;
    position: relative;
    /* background-color: ${ colors.neutralLight }; */
    border-top: 1px solid rgba(40,47,54,0.15);
    border-bottom: 1px solid rgba(40,47,54,0.15);
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    padding: 1.1rem 2.5rem;
    margin-left: -2rem;
    margin-right: -2rem;

    @media ${ device.md } {
      margin-left:  -1rem;
      margin-right:  -1rem;
      padding: 1rem;
    }

    @media ${ device.sm } {
      margin-left: 0;
      margin-right: 0;
      padding: 1rem 1.75rem;
    }

    > div {
      width: 50%;
      font-size: 16px !important;
      font-weight: normal !important;
      letter-spacing: 0;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      opacity: 1;

      &:before {
        content: '';
        display: inline-block;
        margin-right: 0.8rem;
        width: 0.8rem;
        height: 0.7rem;
        background-size: contain;
        background-image: url( ${ CheckSvg });
        background-repeat: no-repeat;
        opacity: 1;
      }

      &:nth-child(2),
      &:nth-child(4) {
        justify-content: flex-end;
      }

      &.room-occupied {
        opacity: 0.5;

        &:before {
          opacity: 0.5;
          background-image: url(${ UncheckSvg });
        }
      }
    }
  }

`

class Calendar extends Component {
  constructor (props) {
    super(props)

    this.state = {
      dates: [],
      statusRowVisible: false
    }
  }

  componentDidMount () {
    const brueckendeckRequest = fetch(brueckendeckUrl).then(response => response.json())
    const inselgartenRequest = fetch(inselgartenUrl).then(response => response.json())
    const sonnendeckRequest = fetch(sonnendeckUrl).then(response => response.json())
    const theaterdeckRequest = fetch(theaterdeckUrl).then(response => response.json())

    Promise.all([brueckendeckRequest, inselgartenRequest, sonnendeckRequest, theaterdeckRequest]).then(values => {
      const result = values.reduce((accumulator, current) => {
        if (current.items) {
          current.items.forEach(item => {
            accumulator.push({
              room: current.summary,
              start: item.start.date,
              end: item.end.date
            })
          })
        }

        return accumulator
      }, [])

      this.setState({
        dates: result
      })
    })
      .catch(err => {
        console.log('Fetch Error:', err)
      })
  }

  cleanStatusRow = (a, b) => {
    Array.prototype.slice.call(document.querySelectorAll('.calendar-status-row')).forEach(row => {
      row.outerHTML = ''
    })
    this.setState({
      statusRowVisible: false
    })
  }

  injectStatusRow = selectedDate => {
    setTimeout(() => {
      const lastInRow = document.querySelector('.react-calendar__tile--active ~ .calendar-last-in-row, .react-calendar__tile--active.calendar-last-in-row')

      const existingRow = lastInRow.nextElementSibling && lastInRow.nextElementSibling.classList.contains('calendar-status-row')

      if (existingRow) {
        document.querySelector('.calendar-status-row').outerHTML = this.generateStatusRow(selectedDate)
        return
      }

      this.cleanStatusRow()

      lastInRow.insertAdjacentHTML('afterend', this.generateStatusRow(selectedDate))

      this.setState({ statusRowVisible: true })
    }, 4)
  }

  getAvailability = (selectedDate, room) => {
    if (!this.state.dates || this.state.dates.length === 0) {
      return false
    }

    return this.state.dates.filter(entry => {
      return (compareDates(entry.start, entry.end, selectedDate)) && entry.room === room
    }).length > 0
  }

  generateStatusRow = selectedDate => {
    return `
      <div class="calendar-status-row">
        <div class="${ this.getAvailability(selectedDate, 'Wiese') && 'room-occupied' }">Wiese</div>
        <div class="${ this.getAvailability(selectedDate, 'Sonnendeck') && 'room-occupied' }">Sonnendeck</div>
        <div class="${ this.getAvailability(selectedDate, 'Brückendeck') && 'room-occupied' }">Brückendeck</div>
        <div class="${ this.getAvailability(selectedDate, 'Theater') && 'room-occupied' }">Theaterdeck</div>
      </div>
    `
  }

  checkDisabled = ({ activeStartDate, date, view }) => {
    if (!this.state.dates || this.state.dates.length === 0) {
      return false
    }

    if (new Date(date).getDay() === 0) {
      return true
    }

    return this.state.dates.filter(entry => compareDates(entry.start, entry.end, date)).length >= 4
  }

  onSelect = selectedDate => {
    this.injectStatusRow(selectedDate)
    if (this.props.onSelectDate) {
      this.props.onSelectDate(selectedDate)
    }
  }

  tagRows = (a, b, c) => {
    return new Date(a.date).getDay() === 0 && 'calendar-last-in-row'
  }

  render () {
    const { statusRowVisible } = this.state
    const { calendarLimit } = this.props

    return (
      <StyledCalendar
        locale="de-DE"
        next2Label={null}
        prev2Label={null}
        showNeighboringMonth={true}
        showFixedNumberOfWeeks={true}
        minDate={new Date()}
        maxDate={new Date(calendarLimit)}
        minDetail={'month'}
        selectRange={false}
        tileDisabled={this.checkDisabled}
        onClickDay={this.onSelect}
        onClickMonth={this.cleanStatusRow}
        tileClassName={this.tagRows}
        onActiveDateChange={this.cleanStatusRow}
        statusRowVisible={statusRowVisible}
      />
    )
  }
}

Calendar.propTypes = {
  // siteTitle: PropTypes.string,
  // currentPathname: PropTypes.string,
}

Calendar.defaultProps = {
  // siteTitle: ``,
}

export default Calendar
export { StyledCalendar }
